import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Slide } from '@mui/material';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import StorePageHeader from 'Components/StorePageHeader';
import { translateLabel } from 'services/Utilities/labelUtils';
import { useActiveCart, usePostCarItem, usePostCartTooling } from 'services/Data/CartService';
import { useDealerPortalFilters, useDealerPortalMenuProductsInfinite } from 'services/Data/DealerPortalMenuServices';
import useImage from 'services/Hooks/useImage';
import { DealerPortalMenuProductTypeEnumValue } from 'enums/DealerPortalMenuProductTypeEnum';
import { DealerPortalSectionEnum, getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import DealerPortalMenuFilterSidebar from './Components/DealerPortalMenuFilterSidebar';
import DealerPortalMenuProductResultsPanel from './Components/DealerPortalMenuProductResultsPanel';

const normalizeCollection = collectionOrWrapped => {
  if (Array.isArray(collectionOrWrapped)) {
    return collectionOrWrapped;
  }

  if (Array.isArray(collectionOrWrapped?.items)) {
    return collectionOrWrapped.items;
  }

  return [];
};

const parseNumberValue = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const getProductType = product => product?.productType || product?.ProductType;

const DealerPortalMenuProducts = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { dealerPortalMenuId, sectionName } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);
  const sectionValue = getDealerPortalSectionValue(sectionName);
  const sectionConfig = DealerPortalSectionEnum.find(section => section.value === sectionValue);
  const showFiltersForSection = Boolean(sectionConfig?.showFilter);
  const {
    getInventoryItemImage,
    getToolingImage,
    getPanelTypeImage,
    getExteriorSignageImage,
    getMerchandisingProgramImage,
  } = useImage();

  const [showRedirectionModal, setShowRedirectionModal] = useState(false);
  const [selectedLeafFilterIds, setSelectedLeafFilterIds] = useState([]);
  const [collapsedFilterNodeIds, setCollapsedFilterNodeIds] = useState([]);
  const loadMoreAnchorRef = useRef(null);
  const [tableDataParams, setTableDataParams] = useState({
    currentPage: 1,
    pageSize: 24,
    search: '',
    sorts: [
      {
        value: 'menuOrder',
        name: t('order'),
        column: 'Order',
        asc: true,
      },
    ],
  });

  const serverTableDataParams = useMemo(
    () => ({
      pageSize: 24,
      sorts: (tableDataParams.sorts || []).map(sortOption => ({
        column: sortOption?.column || 'Order',
        asc: sortOption?.asc !== false,
      })),
    }),
    [tableDataParams.sorts],
  );

  const {
    data: menuProductsPagesData,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useDealerPortalMenuProductsInfinite(
    dealerPortalMenuId,
    serverTableDataParams,
    selectedLeafFilterIds,
    accountReducer.isLoggedIn,
  );
  const { data: menuFiltersData, isLoading: isFiltersLoading } = useDealerPortalFilters(
    dealerPortalMenuId,
    { selectedFilterIds: selectedLeafFilterIds },
    accountReducer.isLoggedIn && showFiltersForSection,
  );
  const { data: cart } = useActiveCart(accountReducer.isLoggedIn);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);
  const { mutate: addCartTooling } = usePostCartTooling(accountReducer.isDemo);

  const buildMenuProductDetailRoute = useCallback(
    productId => `/dealer/section/${sectionName}/menu/${dealerPortalMenuId}/products/${productId}`,
    [sectionName, dealerPortalMenuId],
  );

  const resolvePreviewImageId = useCallback(product => {
    const previewImageId = product?.previewImageId || product?.PreviewImageId;
    if (previewImageId) {
      return previewImageId;
    }

    const productType = getProductType(product);
    if (productType === DealerPortalMenuProductTypeEnumValue.InventoryItem) {
      const images = normalizeCollection(product?.inventoryItem?.images || product?.InventoryItem?.Images);
      return images.find(image => image?.dealerPortalPrimary || image?.DealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.Tooling) {
      const images = normalizeCollection(product?.tooling?.images || product?.Tooling?.Images);
      return images.find(image => image?.dealerPortalPrimary || image?.DealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.ExteriorSignage) {
      const images = normalizeCollection(product?.exteriorSignage?.images || product?.ExteriorSignage?.Images);
      return images.find(image => image?.dealerPortalPrimary || image?.DealerPortalPrimary)?.id || images[0]?.id;
    }

    if (productType === DealerPortalMenuProductTypeEnumValue.MerchandisingProgram) {
      const images = normalizeCollection(product?.merchandisingProgram?.images || product?.MerchandisingProgram?.Images);
      return images.find(image => image?.dealerPortalPrimary || image?.DealerPortalPrimary)?.id || images[0]?.id;
    }

    const images = normalizeCollection(product?.panelType?.images || product?.PanelType?.Images);
    return images.find(image => image?.dealerPortalPrimary || image?.DealerPortalPrimary)?.id || images[0]?.id;
  }, []);

  const getImageSrc = useCallback(
    product => {
      const productType = getProductType(product);
      const productId = product?.productId || product?.ProductId;
      const previewImageId = resolvePreviewImageId(product);

      if (!productId || !previewImageId) {
        return '/images/brp-logo.png';
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.InventoryItem) {
        return getInventoryItemImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.Tooling) {
        return getToolingImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.PanelType) {
        return getPanelTypeImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.ExteriorSignage) {
        return getExteriorSignageImage(productId, previewImageId);
      }

      if (productType === DealerPortalMenuProductTypeEnumValue.MerchandisingProgram) {
        return getMerchandisingProgramImage(productId, previewImageId);
      }

      return '/images/brp-logo.png';
    },
    [
      getInventoryItemImage,
      getToolingImage,
      getPanelTypeImage,
      getExteriorSignageImage,
      getMerchandisingProgramImage,
      resolvePreviewImageId,
    ],
  );

  const menuProductsData = useMemo(() => {
    const pages = normalizeCollection(menuProductsPagesData?.pages);
    if (pages.length === 0) {
      return null;
    }

    const firstPage = pages[0] || {};
    const products = pages.flatMap(page => normalizeCollection(page?.items || page?.Items));

    return {
      dealerPortalMenuId: firstPage?.dealerPortalMenuId || firstPage?.DealerPortalMenuId,
      menuName: firstPage?.menuName || firstPage?.MenuName,
      count: Number(firstPage?.count ?? firstPage?.Count ?? 0),
      products,
    };
  }, [menuProductsPagesData]);

  useEffect(() => {
    const anchorNode = loadMoreAnchorRef.current;
    if (!anchorNode || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px 0px' },
    );

    observer.observe(anchorNode);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, menuProductsData?.products?.length]);

  const configuredProducts = useMemo(() => {
    return normalizeCollection(menuProductsData?.products || menuProductsData?.Products)
      .map(product => {
        const productType = getProductType(product);
        const productId = product?.productId || product?.ProductId;
        const usefulLink = product?.usefulLink || product?.UsefulLink || null;

        const promotionQuantity = parseNumberValue(product?.promotionQuantity ?? product?.PromotionQuantity);
        const isInPromotion = Boolean(product?.isInPromotion ?? product?.IsInPromotion);
        const quantityInCart =
          cart?.cartItems?.find(cartItem => cartItem.inventoryItem_Id === productId || cartItem.tooling_Id === productId)
            ?.quantity ?? 0;

        return {
          id: product?.id || product?.Id || `${productType}:${productId}`,
          order: parseNumberValue(product?.order ?? product?.Order),
          productId,
          productType,
          name: product?.name || product?.Name || '',
          description: product?.description || product?.Description || '',
          shortDescription: product?.shortDescription || product?.ShortDescription || '',
          sku: product?.sku || product?.Sku || null,
          price: parseNumberValue(product?.price ?? product?.Price),
          promotionPrice: parseNumberValue(product?.promotionPrice ?? product?.PromotionPrice),
          currencyName: product?.currencyName || product?.CurrencyName || null,
          quantityPerPackage: parseNumberValue(product?.quantityPerPackage ?? product?.QuantityPerPackage),
          promotionQuantity,
          isInPromotion,
          hasAdditionalToolings: Boolean(product?.hasAdditionalToolings ?? product?.HasAdditionalToolings),
          isPromoLimitReached: isInPromotion && promotionQuantity > 0 && quantityInCart >= promotionQuantity,
          inventoryItem: product?.inventoryItem || product?.InventoryItem || null,
          tooling: product?.tooling || product?.Tooling || null,
          panelType: product?.panelType || product?.PanelType || null,
          exteriorSignage: product?.exteriorSignage || product?.ExteriorSignage || null,
          merchandisingProgram: product?.merchandisingProgram || product?.MerchandisingProgram || null,
          usefulLink,
          imgSrc: getImageSrc(product),
        };
      })
      .filter(product => {
        if (!product.productId || !product.productType) {
          return false;
        }

        return true;
      });
  }, [menuProductsData, cart?.cartItems, getImageSrc]);

  const menuFilters = useMemo(() => normalizeCollection(menuFiltersData), [menuFiltersData]);

  const getFilterLabel = useCallback(
    filter =>
      translateLabel(filter?.name || filter?.Name, i18n.language) ||
      translateLabel(filter?.name || filter?.Name, 'en') ||
      filter?.id ||
      filter?.Id ||
      '',
    [i18n.language],
  );

  const leafFilters = useMemo(() => {
    const leaves = [];

    const walk = (nodes, ancestorIds = []) => {
      nodes.forEach(filter => {
        const filterId = filter?.id || filter?.Id;
        if (!filterId) {
          return;
        }

        const children = normalizeCollection(filter?.children || filter?.Children);
        const nextAncestorIds = [...ancestorIds, filterId];

        if (children.length === 0) {
          leaves.push({
            id: filterId,
            label: getFilterLabel(filter),
            ancestorIds,
          });

          return;
        }

        walk(children, nextAncestorIds);
      });
    };

    walk(menuFilters, []);
    return leaves;
  }, [getFilterLabel, menuFilters]);

  const leafFilterById = useMemo(() => {
    const map = new Map();
    leafFilters.forEach(filter => {
      map.set(filter.id, filter);
    });
    return map;
  }, [leafFilters]);

  useEffect(() => {
    if (leafFilterById.size === 0) {
      return;
    }

    setSelectedLeafFilterIds(currentIds => currentIds.filter(id => leafFilterById.has(id)));
  }, [leafFilterById]);

  const visibleFilterTree = useMemo(() => {
    if (!showFiltersForSection) {
      return [];
    }

    const filterNodes = nodes =>
      nodes
        .map(node => {
          const nodeId = node?.id || node?.Id;
          const children = normalizeCollection(node?.children || node?.Children);
          const visibleChildren = filterNodes(children);

          return {
            ...node,
            id: nodeId,
            children: visibleChildren,
          };
        })
        .sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));

    return filterNodes(menuFilters);
  }, [menuFilters, showFiltersForSection]);

  const allExpandableFilterNodeIds = useMemo(() => {
    const ids = [];

    const collect = nodes => {
      nodes.forEach(node => {
        if (normalizeCollection(node?.children).length > 0) {
          ids.push(node.id);
          collect(normalizeCollection(node.children));
        }
      });
    };

    collect(visibleFilterTree);
    return ids;
  }, [visibleFilterTree]);

  const expandedFilterNodeIds = useMemo(
    () => allExpandableFilterNodeIds.filter(nodeId => !collapsedFilterNodeIds.includes(nodeId)),
    [allExpandableFilterNodeIds, collapsedFilterNodeIds],
  );

  const toggleFilterNodeExpansion = useCallback(nodeId => {
    if (!nodeId) {
      return;
    }

    setCollapsedFilterNodeIds(currentIds =>
      currentIds.includes(nodeId) ? currentIds.filter(existingId => existingId !== nodeId) : [...currentIds, nodeId],
    );
  }, []);

  useEffect(() => {
    setSelectedLeafFilterIds([]);
    setCollapsedFilterNodeIds([]);
  }, [dealerPortalMenuId]);

  const activeFilterChips = useMemo(
    () => selectedLeafFilterIds.map(filterId => leafFilterById.get(filterId)).filter(Boolean),
    [leafFilterById, selectedLeafFilterIds],
  );

  const toggleLeafFilter = useCallback(filterId => {
    if (!filterId) {
      return;
    }

    setSelectedLeafFilterIds(currentIds =>
      currentIds.includes(filterId) ? currentIds.filter(id => id !== filterId) : [...currentIds, filterId],
    );
  }, []);

  const clearSelectedLeafFilters = useCallback(() => {
    setSelectedLeafFilterIds([]);
  }, []);

  const handleAddInventoryItemToCart = useCallback(
    product => {
      const images = normalizeCollection(product?.inventoryItem?.images || product?.inventoryItem?.Images);
      const cartItem = {
        id: uuidv4(),
        inventoryItem_Id: product.productId,
        unitPrice: product.price,
        currencyName: product.currencyName,
        quantity: 1,
        cart_Id: cart?.id,
        unitPricePromotion: product.promotionPrice,
        promotionQuantity: product.promotionQuantity,
        isInPromotion: product.isInPromotion,
        imgSrc: images.find(image => image?.dealerPortalPrimary) || images[0],
        name: product.name,
      };

      addCartItem(cartItem);
      setShowRedirectionModal(true);
    },
    [addCartItem, cart?.id],
  );

  const handleAddToolingToCart = useCallback(
    product => {
      const images = normalizeCollection(product?.tooling?.images || product?.tooling?.Images);
      const cartTooling = {
        id: uuidv4(),
        dateCreated: new Date(),
        quantity: 1,
        tooling_Id: product.productId,
        subTotal: product.price,
        price: product.price,
        name: product.name,
        imgSrc: images.find(image => image?.dealerPortalPrimary) || images[0],
        cart_Id: cart?.id,
      };

      addCartTooling(cartTooling);
      setShowRedirectionModal(true);
    },
    [addCartTooling, cart?.id],
  );

  const menuTitle =
    translateLabel(menuProductsData?.menuName || menuProductsData?.MenuName, i18n.language) ||
    menuProductsData?.dealerPortalMenuId ||
    menuProductsData?.DealerPortalMenuId ||
    t('products');

  const isLoading = isProductsLoading && !menuProductsData;
  const isFiltering = isProductsFetching && !isLoading && !isFetchingNextPage;

  const sortOptions = useMemo(
    () => [
      {
        value: 'menuOrder',
        name: t('filter_order'),
        column: 'Order',
        asc: true,
      },
      {
        value: 'nameAsc',
        name: t('nameAsc'),
        column: 'Name',
        asc: true,
      },
      {
        value: 'nameDesc',
        name: t('nameDesc'),
        column: 'Name',
        asc: false,
      },
      {
        value: 'priceAsc',
        name: `${t('price')} ASC`,
        column: 'Price',
        asc: true,
      },
      {
        value: 'priceDesc',
        name: `${t('price')} DESC`,
        column: 'Price',
        asc: false,
      },
    ],
    [t],
  );

  const totalItemCount = Number(menuProductsData?.count ?? 0);

  const displayedRangeLabel = useMemo(() => {
    if (totalItemCount <= 0) {
      return `0 ${t('of')} 0`;
    }

    if (configuredProducts.length <= 0) {
      return `0 ${t('of')} ${totalItemCount}`;
    }

    return `1-${configuredProducts.length} ${t('of')} ${totalItemCount}`;
  }, [configuredProducts.length, t, totalItemCount]);

  const activeSearchFilter = (tableDataParams.search || '').trim();

  const handleClearSearchFilter = useCallback(() => {
    setTableDataParams(current => ({ ...current, search: '', currentPage: 1 }));
  }, []);

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <CartRedirectionModal
          show={showRedirectionModal}
          onClose={() => setShowRedirectionModal(false)}
          redirectLink={'/dealer/cart'}
          title={t('itemAddedToCart')}
          message={t('itemAddedToCartText')}
        />

        <StorePageHeader title={menuTitle} backgroundColor="lightGray" borderRadius="0.5rem" />

        <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
            <DealerPortalMenuFilterSidebar
              showFiltersForSection={showFiltersForSection}
              selectedLeafFilterIds={selectedLeafFilterIds}
              clearSelectedLeafFilters={clearSelectedLeafFilters}
              activeFilterChips={activeFilterChips}
              isFiltersLoading={isFiltersLoading}
              visibleFilterTree={visibleFilterTree}
              expandedFilterNodeIds={expandedFilterNodeIds}
              onToggleFilterNode={toggleFilterNodeExpansion}
              toggleLeafFilter={toggleLeafFilter}
              getFilterLabel={getFilterLabel}
              titleLabel={t('dealerPortal.filters.title')}
              clearAllLabel={t('dealerPortal.filters.clearAll')}
              selectedLabel={t('dealerPortal.filters.selected')}
              loadingLabel={t('common.loading')}
              noneAvailableLabel={t('dealerPortal.filters.noneAvailable')}
            />

            <DealerPortalMenuProductResultsPanel
              tableDataParams={tableDataParams}
              setTableDataParams={setTableDataParams}
              isLoading={isLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              isFiltering={isFiltering}
              loadMoreAnchorRef={loadMoreAnchorRef}
              sortOptions={sortOptions}
              activeSearchFilter={activeSearchFilter}
              onClearSearchFilter={handleClearSearchFilter}
              filterLabel={t('common.filter')}
              clearLabel={t('common.clear')}
              noItemsLabel={t('noItemsToDisplay')}
              loadingMoreLabel={t('common.loading')}
              displayedRangeLabel={displayedRangeLabel}
              filteredSortedProducts={configuredProducts}
              buildMenuProductDetailRoute={buildMenuProductDetailRoute}
              onNavigateToRoute={route => history.push(route)}
              onAddInventoryItemToCart={handleAddInventoryItemToCart}
              onAddToolingToCart={handleAddToolingToCart}
            />
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default DealerPortalMenuProducts;
